import { request } from "../api";
import { unwrapList } from "./devices";

export type ScheduleDirection = "active_during" | "inactive_during";

export type WorkScheduleAssignmentTargetType = "user" | "device" | "device_group";

export interface WorkScheduleProfile {
  id: string;
  name: string;
  description: string;
  timezone: string;
  schedule_direction: ScheduleDirection;
  is_active: boolean;
  windows_count: number;
  assignments_count: number;
  is_company_fallback: boolean;
  is_currently_enforcing: boolean;
  created_at: string;
  updated_at: string;
}

export interface WorkScheduleWindow {
  id: string;
  weekday: number;
  start_minute: number;
  end_minute: number;
  start_notification_title: string;
  start_notification_body: string;
  end_notification_title: string;
  end_notification_body: string;
  created_at?: string;
  updated_at?: string;
}

export interface WorkScheduleAssignment {
  id: string;
  schedule_profile: string;
  schedule_profile_name: string;
  target_type: WorkScheduleAssignmentTargetType;
  target_id: string;
  target_display: string;
  priority: number;
  valid_from: string | null;
  valid_until: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface WorkScheduleProfileDetail extends WorkScheduleProfile {
  windows: WorkScheduleWindow[];
  assignments: WorkScheduleAssignment[];
}

function qs(params: object): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params) as [string, unknown][]) {
    if (v === undefined || v === "" || v === null) continue;
    sp.append(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

interface Paginated<T> {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results?: T[];
}

// --- Schedules ---
export const schedulesApi = {
  list: async (params: { search?: string; is_active?: boolean } = {}) => {
    const data = await request<Paginated<WorkScheduleProfile> | WorkScheduleProfile[]>(
      `/api/admin/v1/work-schedules/${qs(params)}`,
    );
    return unwrapList(data);
  },
  get: (id: string) =>
    request<WorkScheduleProfileDetail>(`/api/admin/v1/work-schedules/${id}/`),
  create: (body: {
    name: string;
    description?: string;
    timezone?: string;
    schedule_direction?: ScheduleDirection;
    is_active?: boolean;
  }) =>
    request<WorkScheduleProfileDetail>(`/api/admin/v1/work-schedules/`, {
      method: "POST",
      json: body,
    }),
  patch: (
    id: string,
    body: Partial<{
      name: string;
      description: string;
      timezone: string;
      schedule_direction: ScheduleDirection;
      is_active: boolean;
    }>,
  ) =>
    request<WorkScheduleProfileDetail>(`/api/admin/v1/work-schedules/${id}/`, {
      method: "PATCH",
      json: body,
    }),
  delete: (id: string) =>
    request<undefined>(`/api/admin/v1/work-schedules/${id}/`, { method: "DELETE" }),

  listWindows: (id: string) =>
    request<WorkScheduleWindow[]>(`/api/admin/v1/work-schedules/${id}/windows/`),
  createWindow: (
    id: string,
    body: {
      weekday: number; start_minute: number; end_minute: number;
      start_notification_title?: string; start_notification_body?: string;
      end_notification_title?: string; end_notification_body?: string;
    },
  ) =>
    request<WorkScheduleWindow>(`/api/admin/v1/work-schedules/${id}/windows/`, {
      method: "POST",
      json: body,
    }),
  patchWindow: (
    id: string,
    windowId: string,
    body: Partial<{
      weekday: number; start_minute: number; end_minute: number;
      start_notification_title: string; start_notification_body: string;
      end_notification_title: string; end_notification_body: string;
    }>,
  ) =>
    request<WorkScheduleWindow>(
      `/api/admin/v1/work-schedules/${id}/windows/${windowId}/`,
      { method: "PATCH", json: body },
    ),
  deleteWindow: (id: string, windowId: string) =>
    request<undefined>(
      `/api/admin/v1/work-schedules/${id}/windows/${windowId}/`,
      { method: "DELETE" },
    ),

  listAssignments: (id: string) =>
    request<WorkScheduleAssignment[]>(`/api/admin/v1/work-schedules/${id}/assignments/`),
  createAssignment: (
    id: string,
    body: {
      target_type: WorkScheduleAssignmentTargetType;
      target_id: string;
      priority?: number;
      valid_from?: string | null;
      valid_until?: string | null;
      is_active?: boolean;
    },
  ) =>
    request<WorkScheduleAssignment>(`/api/admin/v1/work-schedules/${id}/assignments/`, {
      method: "POST",
      json: body,
    }),
  patchAssignment: (
    scheduleId: string,
    assignmentId: string,
    body: Partial<{
      priority: number;
      valid_from: string | null;
      valid_until: string | null;
      is_active: boolean;
    }>,
  ) =>
    request<WorkScheduleAssignment>(
      `/api/admin/v1/work-schedules/${scheduleId}/assignments/${assignmentId}/`,
      { method: "PATCH", json: body },
    ),
  deleteAssignment: (scheduleId: string, assignmentId: string) =>
    request<undefined>(
      `/api/admin/v1/work-schedules/${scheduleId}/assignments/${assignmentId}/`,
      { method: "DELETE" },
    ),
};
